import { useState } from "react";
import MainLayout from "@/components/layout/MainLayout";
import { useToast } from "@/hooks/use-toast";
import { Button } from "@/components/ui/button";
import { Power, Wrench, History } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Bike } from "@/types/models";
import { useAuth } from "@/context/AuthContext";
import AssignSparePartModal from "@/components/bikes/AssignSparePartModal";
import RepairHistoryModal from "@/components/bikes/RepairHistoryModal";

// Fetch bikes for operator's branch only
const getOperatorBikes = async (branchId: string): Promise<Bike[]> => {
  const { data, error } = await supabase
    .from('bikes')
    .select('*')
    .eq('branch_id', branchId)
    .order('number', { ascending: true });
  
  if (error) throw error;
  return data as Bike[];
};

// Deactivate a bike
const deactivateBike = async (id: string) => {
  const { error } = await supabase
    .from('bikes')
    .update({ active: false })
    .eq('id', id);
  
  if (error) throw error;
  return true;
};

// Activate a bike
const activateBike = async (id: string) => {
  const { error } = await supabase
    .from('bikes')
    .update({ active: true })
    .eq('id', id);
  
  if (error) throw error;
  return true;
};

const BikesOperator = () => {
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [selectedBike, setSelectedBike] = useState<Bike | null>(null);
  const [actionType, setActionType] = useState<'activate' | 'deactivate'>('deactivate');
  const [isAssignSparePartModalOpen, setIsAssignSparePartModalOpen] = useState(false);
  const [isRepairHistoryModalOpen, setIsRepairHistoryModalOpen] = useState(false);
  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { profile } = useAuth();

  // Fetch bikes for operator's branch
  const { data: bikes, isLoading } = useQuery({
    queryKey: ['operator-bikes', profile?.branch_id],
    queryFn: () => getOperatorBikes(profile?.branch_id || ''),
    enabled: !!profile?.branch_id,
  });

  // Deactivate bike mutation
  const deactivateBikeMutation = useMutation({
    mutationFn: deactivateBike,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operator-bikes'] });
      toast({ title: "Bicicleta desactivada correctamente" });
      setIsConfirmDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ 
        variant: "destructive", 
        title: "Error al desactivar bicicleta", 
        description: error.message 
      });
    },
  });

  // Activate bike mutation
  const activateBikeMutation = useMutation({
    mutationFn: activateBike,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['operator-bikes'] });
      toast({ title: "Bicicleta activada correctamente" });
      setIsConfirmDialogOpen(false);
    },
    onError: (error: any) => {
      toast({ 
        variant: "destructive", 
        title: "Error al activar bicicleta", 
        description: error.message 
      });
    },
  });

  const handleDeactivateBike = (bike: Bike) => {
    setSelectedBike(bike);
    setActionType('deactivate');
    setIsConfirmDialogOpen(true);
  };

  const handleActivateBike = (bike: Bike) => {
    setSelectedBike(bike);
    setActionType('activate');
    setIsConfirmDialogOpen(true);
  };

  const handleAssignSparePart = (bike: Bike) => {
    setSelectedBike(bike);
    setIsAssignSparePartModalOpen(true);
  };

  const handleRepairHistory = (bike: Bike) => {
    setSelectedBike(bike);
    setIsRepairHistoryModalOpen(true);
  };

  const confirmAction = () => {
    if (selectedBike) {
      if (actionType === 'deactivate') {
        deactivateBikeMutation.mutate(selectedBike.id);
      } else {
        activateBikeMutation.mutate(selectedBike.id);
      }
    }
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <h1 className="text-2xl font-bold text-gray-800">Mis Bicicletas</h1>
        </div>

        <div className="bg-white shadow-md rounded-lg overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Número</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Modelo</TableHead>
                <TableHead>Color</TableHead>
                <TableHead>Talla</TableHead>
                <TableHead>Serie</TableHead>
                <TableHead>Estado</TableHead>
                <TableHead className="w-[150px]">Acciones</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4">
                    Cargando bicicletas...
                  </TableCell>
                </TableRow>
              ) : bikes?.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-4">
                    No hay bicicletas registradas en tu sucursal
                  </TableCell>
                </TableRow>
              ) : (
                bikes?.map((bike) => (
                  <TableRow key={bike.id}>
                    <TableCell className="py-2">{bike.number}</TableCell>
                    <TableCell className="font-medium py-2">{bike.type}</TableCell>
                    <TableCell className="py-2">{bike.model}</TableCell>
                    <TableCell className="py-2">{bike.color}</TableCell>
                    <TableCell className="py-2">{bike.size || '-'}</TableCell>
                    <TableCell className="py-2">{bike.serial || '-'}</TableCell>
                    <TableCell className="py-2">
                      <Badge variant={bike.active === false ? "destructive" : "default"}>
                        {bike.active === false ? "Inactiva" : "Activa"}
                      </Badge>
                    </TableCell>
                    <TableCell className="py-2">
                      <div className="flex gap-2">
                        <Button variant="outline" size="icon" onClick={() => handleAssignSparePart(bike)} title="Asignar Repuesto">
                          <Wrench size={16} />
                        </Button>
                        <Button variant="outline" size="icon" onClick={() => handleRepairHistory(bike)} title="Historial de Reparaciones">
                          <History size={16} />
                        </Button>
                        {bike.active !== false ? (
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-red-600 hover:text-red-700"
                            onClick={() => handleDeactivateBike(bike)}
                            title="Desactivar"
                          >
                            <Power size={16} />
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="icon"
                            className="text-green-600 hover:text-green-700"
                            onClick={() => handleActivateBike(bike)}
                            title="Activar"
                          >
                            <Power size={16} />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>

      {/* Confirm Action Dialog */}
      <Dialog open={isConfirmDialogOpen} onOpenChange={setIsConfirmDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {actionType === 'deactivate' ? 'Confirmar Desactivación' : 'Confirmar Activación'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            <p>
              ¿Estás seguro que deseas {actionType === 'deactivate' ? 'desactivar' : 'activar'} esta bicicleta?
            </p>
            <p className="font-medium">{selectedBike?.model} - #{selectedBike?.number}</p>
          </div>
          <DialogFooter className="flex justify-end gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => setIsConfirmDialogOpen(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              variant={actionType === 'deactivate' ? "destructive" : "default"}
              onClick={confirmAction}
              disabled={deactivateBikeMutation.isPending || activateBikeMutation.isPending}
            >
              {(deactivateBikeMutation.isPending || activateBikeMutation.isPending) 
                ? 'Procesando...' 
                : (actionType === 'deactivate' ? 'Desactivar' : 'Activar')
              }
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Assign Spare Part Modal */}
      <AssignSparePartModal
        isOpen={isAssignSparePartModalOpen}
        onClose={() => setIsAssignSparePartModalOpen(false)}
        bike={selectedBike}
        onSuccess={() => {
          setIsAssignSparePartModalOpen(false);
          queryClient.invalidateQueries({ queryKey: ['operator-bikes', profile?.branch_id] });
        }}
      />

      {/* Repair History Modal */}
      <RepairHistoryModal
        isOpen={isRepairHistoryModalOpen}
        onClose={() => setIsRepairHistoryModalOpen(false)}
        bike={selectedBike}
      />
    </MainLayout>
  );
};

export default BikesOperator;
